import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { Outlet, useLocation, useNavigate, useOutlet } from "react-router-dom";
import { AppPage } from "../../../components/AppPage";
import { RpiPagination, RpiPaginationRow } from "../../../components/RpiPagination";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { openApiProductColumns } from "../../../configuration/columns/openApiProduct.column";
import { openApiProductFilters } from "../../../configuration/filters/openApiProduct.filter";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiPerPage } from "../../../hooks/useRpiPerPage";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";
import { rpaTheme } from "../../../theme/rpaTheme";
import { OpenApiProductCreateView, OpenApiProductEditView } from "./OpenApiProductView";
import { Box, Group, MantineProvider, Menu } from "@mantine/core";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { Button } from "@blurb/codex-react";
import { IconV3, useShowSuccessToast } from "@rpi/openapi-core";
import { RpaTableMenu } from "../../../components/RpaTableMenu";
import { ConfirmationModalV2 } from "../../../components/modals/ConfirmationModalV2";

export const OpenApiProductsView: React.FC = () => {
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.OpenApiProductsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.OpenApiProductsQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.OpenApiProductsQueryVariables>>();
  const [isSyncModalOpen, setIsSyncModalOpen] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | undefined>();

  const pagination = useRpiPagination({ perPage: 10 });

  const showSuccessToast = useShowSuccessToast();
  const { mutateAsync: syncStandardProducts, ...syncMutation } = GraphQL.useAdminSyncStandardProductsMutation();

  const { data, isLoading, error, refetch } = GraphQL.useOpenApiProductsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.openapiProducts.totalCount),
    }
  );
  const openApiProducts = React.useMemo(() => data?.openapiProducts.data, [data]);

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  const handleSyncSkus = React.useCallback(async () => {
    setSyncError(undefined);
    try {
      await syncStandardProducts({});
      showSuccessToast("All SKU data has been synced from the source of truth.");
      setIsSyncModalOpen(false);
      refetch();
    } catch (error: any) {
      setSyncError("An error occurred while syncing SKUs.");
    }
  }, [syncStandardProducts, refetch, showSuccessToast]);

  const location = useLocation();
  const isCreateOpen = location.pathname.includes(`/create`);
  const isEditOpen = location.pathname.includes(`/edit/`);
  const navigate = useNavigate();

  return (
    <AppPage path={["Open API Products"]} onReload={refetch}>
      <MantineProvider theme={rpaTheme}>
        <OpenApiProductCreateView
          isOpened={isCreateOpen}
          onClose={() => {
            navigate("./");
          }}
        >
          <Outlet />
        </OpenApiProductCreateView>

        <OpenApiProductEditView isOpened={isEditOpen} onClose={() => navigate("./")}>
          <Outlet />
        </OpenApiProductEditView>
        {!isCreateOpen && !isEditOpen && outlet}
      </MantineProvider>

      <AppPage.Section>
        <Group position={"apart"}>
          <RpiSearchFilter
            placeholder="Search by Name"
            filters={openApiProductFilters}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
          <MantineProvider theme={rpaTheme}>
            <Group spacing={"xs"}>
              <Button
                variant="outlined"
                intent="brand"
                onClick={() => setIsSyncModalOpen(true)}
                data-testid="syncSkusButton"
              >
                <Group spacing={"xs"}>
                  <IconV3 icon="refresh" color="inherit" /> Sync SKUs
                </Group>
              </Button>
              <Button
                variant="filled"
                intent="brand"
                onClick={() => {
                  navigate("create");
                }}
                data-testid="addNewSkuButton"
              >
                <Group spacing={"xs"}>
                  <IconV3 icon="add" color="inherit" /> Add New SKU
                </Group>
              </Button>
            </Group>
          </MantineProvider>
        </Group>
        <Box>
          <RpiTable
            emptyMessage={
              searchInputs?.length || searchGeneral
                ? "No open API products found."
                : "Open API products will appear here."
            }
            isLoading={isLoading}
            error={error}
            perPage={pagination.perPage}
            data={openApiProducts}
            columns={openApiProductColumns}
            additionalColumns={[
              {
                header: "",
                maxSize: 36,
                minSize: 36,
                size: 36,
                align: "center",
                noPadding: true,
                cell(data) {
                  return <OpenApiProductsActionMenu skuId={data.row.id} />;
                },
              },
            ]}
            withSortBy={{ onChange: setSortInput }}
          />
        </Box>

        <RpiPaginationRow>
          <RpiPagination pagination={pagination} />
          <RpiPerPage perPage={pagination.perPage} setPerPage={pagination.setPerPage} />
        </RpiPaginationRow>
      </AppPage.Section>

      <ConfirmationModalV2
        opened={isSyncModalOpen}
        onClose={() => {
          setIsSyncModalOpen(false);
          setSyncError(undefined);
        }}
        onConfirm={handleSyncSkus}
        isLoading={syncMutation.isLoading}
        error={syncError}
        title="Sync SKUs from Source of Truth"
        message="This will sync the product offering's source of truth with the database. All SKU data in the source of truth will overwrite the matching SKUs in the database. Do you wish to proceed?"
        loadingLabel="Syncing..."
      />
    </AppPage>
  );
};

type OpenApiProductsActionMenuProps = {
  skuId: number;
};

function OpenApiProductsActionMenu({ skuId }: OpenApiProductsActionMenuProps) {
  const navigateRoute = useNavigateRoute();
  const navigate = useNavigate();
  return (
    <RpaTableMenu>
      <Menu.Item
        icon={<IconV3 icon="description" size={20} />}
        onClick={() => navigateRoute(["open-api-product", { id: skuId }])}
      >
        View Details
      </Menu.Item>
      <Menu.Item icon={<IconV3 icon="edit" size={20} />} onClick={() => navigate(`edit/${skuId}`)}>
        Edit SKU
      </Menu.Item>
    </RpaTableMenu>
  );
}
